/**
 * HITL 게이트 — **이 시스템의 유일한 비가역 확인**(발주).
 *
 * 규율 셋:
 *  ① 승인은 한 번만 — 클릭 즉시 버튼을 걷는다(스토어가 `clearGate`). 여기서는 그 사이의
 *     `approving` 상태를 눈에 보이게 잠근다(DESIGN §4 게이트 상태 전이).
 *  ② 반려에는 **사유가 붙는다** — 비대칭 계약의 절반이고, 사유 없이 반려하면 에이전트가
 *     무엇을 고쳐야 하는지 모른다.
 *  ③ 되돌릴 수 없다는 사실을 문구로 말한다 — 백엔드가 준 question 을 그대로 보여준다.
 *
 * 형태에 의미가 있다(P3′): 게이트는 danger **채움** 카드다. 같은 빨강이라도 시스템 오류는
 * **외곽선** 카드로 그린다 — "되돌릴 수 없음"과 "실패했음"을 색이 아니라 형태로 가른다.
 * 승인 후 '전송됨' 같은 완료 표기는 여기서 만들지 않는다: 실제 발주 실행은 백엔드가 이어받고,
 * 실행되지 않은 비가역 작업을 완료로 표시하는 것이 P1 위반이다.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { GateRequest } from './events'
import type { GateDecision } from './api'

export function GateCard({
  gate,
  onDecide,
  busy,
}: {
  gate: GateRequest
  onDecide: (decision: GateDecision) => void
  busy: boolean
}) {
  const { t } = useTranslation()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const decide = (decision: GateDecision) => {
    setSubmitting(true)
    onDecide(decision)
  }
  const locked = busy || submitting

  return (
    <section className="card gate">
      <div className="card-head">
        <span className="chip dangerc">{t('gate.badge')}</span>
      </div>

      <p className="gate-question">{gate.question}</p>

      {gate.order && Object.keys(gate.order).length > 0 && (
        <dl className="gate-grid">
          {Object.entries(gate.order).map(([key, value]) => (
            <div key={key} className="contents">
              <dt>{key}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {!rejecting ? (
        <>
          <p className="gate-note">{t('gate.irreversible')}</p>
          <div className="gate-actions">
            <button
              type="button"
              className="btn danger"
              disabled={locked}
              onClick={() => decide({ approved: true })}
            >
              {submitting && <span className="spinner sm" aria-hidden="true" />}
              {submitting ? t('gate.approving') : t('gate.approve')}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={locked}
              onClick={() => setRejecting(true)}
            >
              {t('gate.reject')}
            </button>
          </div>
        </>
      ) : (
        <div className="gate-reason">
          <label htmlFor="gate-reason">{t('gate.reject_hint')}</label>
          <input
            id="gate-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('gate.reject_placeholder')}
          />
          <div className="gate-actions">
            <button
              type="button"
              className="btn solid"
              disabled={locked || !reason.trim()}
              onClick={() => decide({ approved: false, reason: reason.trim() })}
            >
              {t('gate.reject_send')}
            </button>
            <button type="button" className="btn ghost" onClick={() => setRejecting(false)}>
              {t('gate.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
